import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line, ComposedChart, ReferenceLine, Legend } from 'recharts';
import { Sun, Zap, TrendingUp, TrendingDown, RefreshCw, Calendar, AlertTriangle, BatteryCharging, ArrowDown, ArrowUp, CloudSun, Minus } from 'lucide-react';
// --- Mock Data ---
const solarData = Array.from({
  length: 24
}, (_, i) => {
  const hour = i;
  // Solar curve (bell curve centered at 13:00)
  const solarPotential = hour >= 6 && hour <= 20 ? Math.max(0, Math.sin((hour - 6) / 14 * Math.PI) * 1000) : 0;
  const cloudCover = Math.random() * 0.2; // 0-20% cloud cover noise
  const irradiance = solarPotential * (1 - cloudCover);
  const generation = irradiance / 1000 * 15; // 15GW capacity
  const demand = 25 + (hour >= 8 && hour <= 20 ? 10 : 0) + Math.sin(hour / 4) * 2;
  const netLoad = demand - generation;
  // Price inverse to solar (duck curve effect)
  const price = 80 - generation * 2 + demand * 0.5 + Math.random() * 5;
  return {
    time: `${hour}:00`,
    irradiance: Math.round(irradiance),
    generation: parseFloat(generation.toFixed(1)),
    demand: parseFloat(demand.toFixed(1)),
    netLoad: parseFloat(netLoad.toFixed(1)),
    price: Math.round(price),
    curtailment: generation > 12 ? parseFloat((generation - 12).toFixed(1)) : 0
  };
});
const regionalIrradiance = [{
  region: 'Andalucía',
  value: 980,
  trend: 'up'
}, {
  region: 'Extremadura',
  value: 945,
  trend: 'up'
}, {
  region: 'Castilla-La Mancha',
  value: 890,
  trend: 'stable'
}, {
  region: 'Murcia',
  value: 920,
  trend: 'up'
}, {
  region: 'Aragón',
  value: 780,
  trend: 'down'
}];
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-amber-100 rounded-xl p-4 shadow-xl z-50" data-id="element-3099">
        <p className="text-xs font-bold text-slate-900 mb-2" data-id="element-3100">{label}</p>
        <div className="space-y-1" data-id="element-3101">
          {payload.map((entry: any) => <div key={entry.name} className="flex items-center justify-between gap-4" data-id="element-3102">
              <div className="flex items-center gap-2" data-id="element-3103">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: entry.color
            }} data-id="element-3104" />
                <span className="text-xs text-slate-600 capitalize" data-id="element-3105">
                  {entry.name}
                </span>
              </div>
              <span className="text-xs font-mono font-medium text-slate-900" data-id="element-3106">
                {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                {entry.unit || ''}
              </span>
            </div>)}
        </div>
      </div>;
  }
  return null;
};
export function SpainSolarDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-amber-50/30" data-id="element-3107">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3108">
        <div data-id="element-3109">
          <div className="flex items-center gap-2 mb-1" data-id="element-3110">
            <div className="p-2 bg-amber-500 rounded-lg" data-id="element-3111">
              <Sun size={20} className="text-white" data-id="element-3112" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-3113">
              Spain Solar Analysis
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-3114">
            Irradiance forecasts, PV generation, and duck curve analysis
          </p>
        </div>

        <div className="flex items-center gap-3 ml-11 md:ml-0" data-id="element-3115">
          <button className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-3116">
            <Calendar size={16} data-id="element-3117" />
            <span data-id="element-3118">Today, Oct 24</span>
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors shadow-sm shadow-amber-500/20" data-id="element-3119">
            <RefreshCw size={16} data-id="element-3120" />
            <span data-id="element-3121">Update Forecast</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3122">
        {[{
        title: 'Peak Irradiance',
        value: '985',
        unit: 'W/m²',
        change: '+5.2%',
        trend: 'up',
        icon: Sun,
        color: 'amber'
      }, {
        title: 'PV Generation',
        value: '14.2',
        unit: 'GW',
        change: '+8.4%',
        trend: 'up',
        icon: Zap,
        color: 'yellow'
      }, {
        title: 'Curtailment Risk',
        value: 'High',
        unit: '',
        change: '13:00-15:00',
        trend: 'warning',
        icon: AlertTriangle,
        color: 'rose'
      }, {
        title: 'Min Net Load',
        value: '12.4',
        unit: 'GW',
        change: '-12%',
        trend: 'down',
        icon: BatteryCharging,
        color: 'orange'
      }].map((metric, idx) => <motion.div key={metric.title} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-5 shadow-sm hover:shadow-md transition-all group" data-id="element-3123">
            <div className="flex justify-between items-start mb-4" data-id="element-3124">
              <div className={`p-2 rounded-lg bg-${metric.color}-50 text-${metric.color}-600 group-hover:bg-${metric.color}-100 transition-colors`} data-id="element-3125">
                <metric.icon size={20} data-id="element-3126" />
              </div>
              <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${metric.trend === 'up' ? 'bg-emerald-50 text-emerald-700' : metric.trend === 'down' ? 'bg-blue-50 text-blue-700' : 'bg-rose-50 text-rose-700'}`} data-id="element-3127">
                {metric.trend === 'up' ? <TrendingUp size={12} data-id="element-3128" /> : metric.trend === 'down' ? <TrendingDown size={12} data-id="element-3129" /> : <AlertTriangle size={12} data-id="element-3130" />}
                {metric.change}
              </div>
            </div>
            <div data-id="element-3131">
              <p className="text-sm text-slate-500 font-medium" data-id="element-3132">
                {metric.title}
              </p>
              <div className="flex items-baseline gap-1 mt-1" data-id="element-3133">
                <span className={`text-2xl font-bold ${metric.trend === 'warning' ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-3134">
                  {metric.value}
                </span>
                <span className="text-sm text-slate-400 font-medium" data-id="element-3135">
                  {metric.unit}
                </span>
              </div>
            </div>
          </motion.div>)}
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3136">
        {/* Duck Curve Analysis */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3137">
          <div className="flex items-center justify-between mb-6" data-id="element-3138">
            <div data-id="element-3139">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-3140">
                Duck Curve Analysis
              </h3>
              <p className="text-sm text-slate-500" data-id="element-3141">
                Net load vs. Solar generation impact
              </p>
            </div>
            <div className="flex gap-2" data-id="element-3142">
              <span className="px-2 py-1 bg-amber-50 rounded text-xs font-medium text-amber-700 border border-amber-100" data-id="element-3143">
                Peak Solar: 13:00
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-3144">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3145">
              <ComposedChart data={solarData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-3146">
                <defs data-id="element-3147">
                  <linearGradient id="colorSolar" x1="0" y1="0" x2="0" y2="1" data-id="element-3148">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} data-id="element-3149" />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} data-id="element-3150" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-3151" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} dy={10} data-id="element-3152" />
                <YAxis yAxisId="left" orientation="left" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} label={{
                value: 'Power (GW)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fill: '#64748b',
                  fontSize: 12
                }
              }} data-id="element-3153" />
                <Tooltip content={<CustomTooltip data-id="element-3155" />} data-id="element-3154" />
                <Legend iconType="circle" wrapperStyle={{
                paddingTop: '20px'
              }} data-id="element-3156" />
                <Area yAxisId="left" type="monotone" dataKey="generation" name="Solar Generation" stroke="#f59e0b" fill="url(#colorSolar)" strokeWidth={2} data-id="element-3157" />
                <Line yAxisId="left" type="monotone" dataKey="demand" name="Total Demand" stroke="#0f172a" strokeWidth={2} dot={false} data-id="element-3158" />
                <Line yAxisId="left" type="monotone" dataKey="netLoad" name="Net Load" stroke="#6366f1" strokeWidth={2} dot={false} strokeDasharray="5 5" data-id="element-3159" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Regional Irradiance */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm flex flex-col" data-id="element-3160">
          <div className="mb-6" data-id="element-3161">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-3162">
              Regional Irradiance
            </h3>
            <p className="text-sm text-slate-500" data-id="element-3163">Current W/m² by region</p>
          </div>

          <div className="flex-1 space-y-5" data-id="element-3164">
            {regionalIrradiance.map(reg => <div key={reg.region} data-id="element-3165">
                <div className="flex justify-between text-sm mb-2" data-id="element-3166">
                  <span className="font-medium text-slate-700" data-id="element-3167">
                    {reg.region}
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-3168">
                    {reg.value} W/m²
                  </span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-3169">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${reg.value / 1200 * 100}%`
              }} transition={{
                duration: 1,
                ease: 'easeOut'
              }} className={`h-full rounded-full ${reg.value > 900 ? 'bg-amber-500' : reg.value > 800 ? 'bg-yellow-500' : 'bg-orange-400'}`} data-id="element-3170" />
                </div>
                <div className="flex justify-between mt-1" data-id="element-3171">
                  <span className="text-[10px] text-slate-400" data-id="element-3172">
                    Forecast: Clear Sky
                  </span>
                  <span className={`text-[10px] font-medium flex items-center gap-1 ${reg.trend === 'up' ? 'text-emerald-600' : reg.trend === 'down' ? 'text-rose-600' : 'text-slate-500'}`} data-id="element-3173">
                    {reg.trend === 'up' ? <ArrowUp size={10} data-id="element-3174" /> : reg.trend === 'down' ? <ArrowDown size={10} data-id="element-3175" /> : <Minus size={10} data-id="element-3176" />}
                    {reg.trend === 'up' ? 'Rising' : reg.trend === 'down' ? 'Falling' : 'Stable'}
                  </span>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3177">
        {/* Price Impact */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3178">
          <div className="mb-6 flex justify-between items-center" data-id="element-3179">
            <div data-id="element-3180">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-3181">
                Price Cannibalization
              </h3>
              <p className="text-sm text-slate-500" data-id="element-3182">
                Solar impact on spot prices
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium" data-id="element-3183">
              <span className="flex items-center gap-1 text-amber-600" data-id="element-3184">
                <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-3185" /> Solar (GW)
              </span>
              <span className="flex items-center gap-1 text-slate-600" data-id="element-3186">
                <div className="w-2 h-2 rounded-full bg-slate-500" data-id="element-3187" /> Price (€)
              </span>
            </div>
          </div>

          <div className="h-[250px] w-full" data-id="element-3188">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3189">
              <ComposedChart data={solarData} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-3190">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-3191" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} interval={3} data-id="element-3192" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-3193" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-3194" />
                <Tooltip content={<CustomTooltip data-id="element-3196" />} data-id="element-3195" />
                <Area yAxisId="left" type="monotone" dataKey="generation" name="Solar Gen" fill="url(#colorSolar)" stroke="#f59e0b" data-id="element-3197" />
                <Line yAxisId="right" type="monotone" dataKey="price" name="Spot Price" stroke="#475569" strokeWidth={2} dot={false} data-id="element-3198" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Curtailment Risk */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3199">
          <div className="mb-6" data-id="element-3200">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-3201">
              Curtailment Analysis
            </h3>
            <p className="text-sm text-slate-500" data-id="element-3202">Excess generation forecast</p>
          </div>

          <div className="h-[250px] w-full" data-id="element-3203">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3204">
              <BarChart data={solarData.filter(d => d.curtailment > 0 || parseInt(d.time) >= 10 && parseInt(d.time) <= 16)} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-3205">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-3206" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-3207" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-3208" />
                <Tooltip content={<CustomTooltip data-id="element-3210" />} data-id="element-3209" />
                <Bar dataKey="curtailment" name="Excess (GW)" fill="#f43f5e" radius={[4, 4, 0, 0]} data-id="element-3211" />
                <ReferenceLine y={0} stroke="#e2e8f0" data-id="element-3212" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </div>;
}